import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { STATUS_LABELS } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, ArrowRight, Boxes, CheckCircle2, ClipboardList, Clock, Factory, PackageOpen, Send, Truck, Warehouse, Zap } from "lucide-react";

const KPI_LIST = [
  { key: "received_today", label: "Ricevuti oggi", icon: PackageOpen, color: "#d63384" },
  { key: "total_open_items", label: "Pratiche aperte", icon: ClipboardList, color: "#6f42c1" },
  { key: "waiting_verification", label: "In verifica iniziale", icon: Zap, color: "#0ea5e9" },
  { key: "ready_to_ship", label: "Pronti per spedizione", icon: Truck, color: "#0891b2" },
  { key: "at_manufacturer", label: "Presso produttore", icon: Factory, color: "#7c3aed" },
  { key: "waiting_approval", label: "In attesa autorizzazione", icon: Clock, color: "#d97706" },
  { key: "at_ama", label: "Presenti in AMA", icon: Warehouse, color: "#0a0a2e" },
  { key: "ready_pickup", label: "Pronti per il ritiro", icon: CheckCircle2, color: "#059669" },
];

export default function Dashboard() {
  const { user, hasRole } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [services, setServices] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    (async () => {
      const [d, s, r] = await Promise.all([
        api.get("/dashboard"), api.get("/dashboard/services-status"), api.get("/repairs/items?limit=8"),
      ]);
      setData(d.data); setServices(s.data); setItems(r.data);
    })();
  }, []);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#d63384]">AMA ServiceFlow</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#0a0a2e]">Ciao, {user?.full_name?.split(" ")[0] || "Utente"}.</h1>
          <p className="text-slate-500 mt-1">Panoramica operativa dell'assistenza tecnica.</p>
        </div>
        {hasRole("reception") && (
          <Button data-testid="dashboard-new-intake-btn" className="ama-btn-primary rounded-full" onClick={() => nav("/accettazione")}>
            <ClipboardList className="w-4 h-4 mr-2" /> Nuova accettazione
          </Button>
        )}
      </div>

      {/* Services banner */}
      {services && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <div className={`ama-card p-3 flex items-center gap-3 ${services.ocr.configured ? "" : "border-amber-200 bg-amber-50/50"}`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${services.ocr.configured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
              <Zap className="w-4 h-4" />
            </div>
            <div className="flex-1 text-sm">
              <div className="font-semibold text-[#0a0a2e]">OCR etichette (Gemini)</div>
              <div className="text-xs text-slate-500" data-testid="dashboard-ocr-status">{services.ocr.configured ? "Servizio configurato e operativo" : "Servizio non configurato"}</div>
            </div>
          </div>
          <div className={`ama-card p-3 flex items-center gap-3 ${services.email.configured ? "" : "border-amber-200 bg-amber-50/50"}`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${services.email.configured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
              <Send className="w-4 h-4" />
            </div>
            <div className="flex-1 text-sm">
              <div className="font-semibold text-[#0a0a2e]">Invio email (Resend)</div>
              <div className="text-xs text-slate-500" data-testid="dashboard-email-status">{services.email.configured ? "Servizio configurato" : "Email non ancora configurata — la comunicazione sarà registrata come bozza."}</div>
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="dashboard-kpis">
        {KPI_LIST.map((k) => {
          const val = data?.kpis?.[k.key] ?? "–";
          const Icon = k.icon;
          return (
            <div key={k.key} className="ama-card p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">{k.label}</div>
                <Icon className="w-4 h-4" style={{ color: k.color }} />
              </div>
              <div className="text-3xl font-heading font-bold text-[#0a0a2e]" data-testid={`kpi-${k.key}`}>{val}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 ama-card border-0 shadow-none">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-heading">Ultimi prodotti in assistenza</CardTitle>
            <Link to="/prodotti-in-riparazione" className="text-sm text-[#d63384] hover:underline inline-flex items-center gap-1">
              Tutti <ArrowRight className="w-3 h-3" />
            </Link>
          </CardHeader>
          <CardContent className="space-y-2">
            {items.length === 0 && <div className="text-sm text-slate-500 italic py-6 text-center">Nessun prodotto ancora registrato.</div>}
            {items.map((it) => (
              <Link to={`/prodotti/${it.id}`} key={it.id} className="flex items-center gap-3 rounded-lg p-2 hover:bg-slate-50 transition-colors" data-testid={`recent-item-${it.item_number}`}>
                <div className="w-9 h-9 rounded-lg bg-fuchsia-50 flex items-center justify-center text-[#d63384]"><Boxes className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#0a0a2e] truncate">{it.item_number} — {it.manufacturer_name} {it.model}</div>
                  <div className="text-xs text-slate-500 truncate">{it.customer_name} · SN {it.serial_number || "-"}</div>
                </div>
                <div className="text-xs">{STATUS_LABELS[it.current_status]}</div>
              </Link>
            ))}
          </CardContent>
        </Card>

        <Card className="ama-card border-0 shadow-none">
          <CardHeader><CardTitle className="text-lg font-heading">Per stato</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data?.by_status?.slice(0, 10).map((s) => (
                <div key={s.status} className="flex items-center gap-2">
                  <div className="flex-1 text-sm text-slate-700 truncate">{STATUS_LABELS[s.status] || s.status}</div>
                  <div className="text-sm font-semibold text-[#0a0a2e]">{s.count}</div>
                </div>
              )) || <div className="text-sm text-slate-500 italic">Nessun dato.</div>}
            </div>
            {data?.by_manufacturer?.length > 0 && (
              <>
                <div className="mt-6 mb-2 text-xs uppercase tracking-wider font-semibold text-slate-500">Per produttore</div>
                <div className="space-y-2">
                  {data.by_manufacturer.slice(0, 6).map((m) => (
                    <div key={m.name} className="flex items-center gap-2">
                      <div className="flex-1 text-sm text-slate-700 truncate">{m.name}</div>
                      <div className="text-sm font-semibold text-[#0a0a2e]">{m.count}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
